import {toHeaderCase, observable, computed, _, pluralize, logJS} from '@api'
import {CatalogImageSourceConfig, CatalogStore, DeploymentConfig, DetailsViewConfig, DetailsViewFieldConfig, globalStores, ICatalogFeaturesConfig, ICatalogFieldConfig, ICatalogPresetES, LibraryItemConfig, LibraryItemConfigWrapperStore, ModalConfig, ModalTabConfig, nav, SidebarConfig, ui, WorkspaceConfig, WorkspacesStore} from '@stores'
import {removeUndefinedsNested} from '@utils'
import {action, autorun, toJS, trace} from 'mobx'

export const DEBUG_WORKSPACES = DEBUG && false

export function shouldIgnoreCatalogField(ws: Partial<WorkspaceConfig>, field: string) {
  return _.includes(['exId', 'images', 'id', 'exLastSync'], field) //, ..._.flatMap((ws.seasonSelectors ?? []).map(s => (s.fields ?? []).map(f => f.field)))], field)
}

export interface IWorkspaceForeignKeyDescriptor {
  parent: Workspace
  child: Workspace
  field: ICatalogFieldConfig
}

export class Workspace {
  constructor(public workspaces: WorkspacesStore, config: WorkspaceConfig, public deploymentConfig: DeploymentConfig) {
    this.config = _.cloneDeep(config)

    if (config.catalog) {
      this.catalog = new CatalogStore(this, globalStores)
      this._toDispose = [
        autorun(() => {
          const {config, catalog: {fieldByWorkspaceIndex}} = this
          // trace()
          this._sanitizeConfig()
        }, {name: `Sanitize workspace config for ${config._id}`})
      ]
    }
  }

  @computed get defaultVisibleCardFields() {
    return this.catalog.defaultFieldsFor(this)
  }

  @computed get foreignKeys() : Array<IWorkspaceForeignKeyDescriptor> {
    const {catalog, catalog: {findFieldInTree, catalogConfig: {fields}}, workspaces: {wrapperByIndex}} = this,
      fkFields = fields.filter(f => f.esIndex != null)

    // console.log(toJS(fkFields), fkFields.map(f => f.field))
    return fkFields.map(field => ({
      field,
      child: wrapperByIndex[field.esIndex],
      parent: wrapperByIndex[findFieldInTree(field.field, true)?.esIndex ?? catalog.index],
    }))
  }

  @computed get objectFit() {
    return this.config?.catalog?.features?.card?.objectFit
  }

  @computed get visible() {
    const {config: {visible}, workspaces: {deploymentConfig}} = this

    if (deploymentConfig?.settings?.security?.isWorkspaceVisible) {
      const {settings: {security: {isWorkspaceVisible}}} = deploymentConfig

      if (_.isFunction(isWorkspaceVisible)) {
        const r = isWorkspaceVisible(this, globalStores)
        if (!r) return false
      }
    }

    if (_.isBoolean(visible)) return visible
    if (_.isFunction(visible)) {
      return visible(globalStores)
    }

    return true
  }

  @computed get section() { return this.catalog ? this.catalog.workspaceConfig.sections.find(s => s.workspace?._id == this._id) : null}

  @computed get url() { return nav.urlFor_string({workspace: this.config}) }

  get title() { return this.config.title}

  get shortTitle() { return this.config.shortTitle}

  get _id() { return this.config._id}

  get dataMap() { return this.config.dataMap }

  @computed get active() { return this._id === this.workspaces.current}

  @observable config: WorkspaceConfig
  catalog: CatalogStore

  _toDispose = []

  dispose = () => {
    this._toDispose.forEach(f => f())
  }

  get workspacesByIndex() {
    return this.workspaces.workspaceByIndex
  }

  @computed get librariesByIndex() { return _.groupBy(this.config?.libraries ?? [], l => l.index)}

  @computed get fieldsByName() {
    return _.keyBy(this.config.catalog?.fields ?? [], f => f.field)
  }

  @computed get debugLabel() {
    const {config: {_id, title, itemName, catalog}} = this

    return `workspace '${title}' - ${catalog?.index ?? '--'} - ${_id} - ${itemName}`
  }

  @observable sanitized = false

  @action _sanitizeConfig = () => {
    this.sanitized = true

    const {config} = this

    if (!config.libraries) {
      config.libraries = []
    }

    if (this.deploymentConfig.settings?.sanitize === false) {
      return
    }

    if (config.catalog?.tagType && config.catalog?.dragType && this.catalog._fieldByName[config.catalog.dragType?.field] && !config.libraries.find(li => li.name == 'tags')) {
      config.libraries.push({
        name: 'tags',
        label: 'Tags',
        filter: false,
        editable: true,
        index: 'vip-tag',
        renderer: 'chip',
        assignTo: config.catalog.dragType?.field,
        catalogFields: [config.catalog.dragType?.field],
        sortField: 'name',
        def: {}
      })
    }

    if (config?.catalog?.index) {
      DEBUG && DEBUG_WORKSPACES && console.groupCollapsed(`Sanitizing ${this.debugLabel}`)

      if (config?.catalog?.index && !config?.catalog?.features?.noSelfLibrary) {
        const siteLibraryLabel = `${config.title}`
        if (!config.libraries.some(l => l.label === siteLibraryLabel)) {
          const siteLibrary = {addedBySanitizer: true, label: siteLibraryLabel, index: config.catalog.index, name: `${config._id}`}
          DEBUG && DEBUG_WORKSPACES && console.log(`Created library '${siteLibrary.label}'`)
          config.libraries = [siteLibrary, ...config.libraries]
        }
      }

      // if (config.catalog && !config.catalog.features?.actionButtons) {
      //   //_.merge(config.catalog, {features: {actionButtons: {create: true, duplicate: true, delete: true, uploadImages: true, toggleActive: true, changeColor: true }} as Partial<ICatalogFeaturesConfig> })
      //   _.merge(config.catalog, } as Partial<ICatalogFeaturesConfig>})
      //   DEBUG && LOG_SANITIZE && console.log(`No actionButtons specified, defaulting to: `, toJS(config.catalog.features?.actionButtons))
      // }

      config.customViews = _.uniqBy([...(config.customViews ?? []), ...(config.presets ?? [])], cv => cv._id)

      const {workspaces, catalog: {workspaceConfig, labelForField}, catalog} = this,
        imageField = workspaceConfig.dataMap?.images ?? 'images'

      if (config.catalog.fields && !catalog._fieldByName[imageField]) {
        const hasImageField: ICatalogFieldConfig = {
          field: imageField,
          label: 'Has Image',
          sort: {
            subField: 'exId'
          },
          filter: true,
          card: {
            hide: true
          },
          agg: 'exists',
          group: {},
          def: {
            type: 'Boolean'
          }
        }

        config.catalog.fields.push(hasImageField)

        DEBUG && DEBUG_WORKSPACES && console.log(`Added ${hasImageField.label} field`)
      }

      if (_.isEmpty(config.catalog.imageSources) || !config.catalog.imageSources.some(is => is.id === config._id || is.field === imageField)) {
        const imageSource = {id: config._id, field: imageField, label: catalog.catalogItemName}
        config.catalog.imageSources = [...(config.catalog.imageSources ?? []), imageSource]
        DEBUG && DEBUG_WORKSPACES && console.log(`Added image source '${imageSource.label}' - ${imageSource.id}`, imageSource)
      }

      config.libraries.forEach(l => {
        if (!l.workspaceId && l.index) {
          l.workspaceId = _.first(this.workspacesByIndex[l.index])?._id // todo - this will break as soon as we have multiple workspaces with the same index
        }
      });

      (this.config.catalog.fields ?? []).forEach((f: ICatalogFieldConfig) => {
        if (f?.def?.enumValues) {
          if (_.isUndefined(f.sort)) {
            f.sort = {
              catalogItem: true,
              custom: {
                _key: {order: f.def.enumValues}
              }
            }
          }
        }
      })

      /**
       * Traverse the 1-off linked entities (refs and refLists) and add their fields (and subsequent foreign links)
       */
      _.orderBy(this.catalog.foreignKeyFields, f => labelForField(f))                       // Iterate through any linked workspace fields
        .filter(f =>
          /*!f.__sanitizer?.imported &&*/    //
                                             // If we skip already seen workspac
                                             // That we haven't already added during the sanitize process
          !f.hidden)
        .forEach(f => {
          (this.workspacesByIndex[f.esIndex] ?? [])
            //.filter(ws => ws.config.visible !== false)
            .forEach(associatedWorkspace => {                 // For any workspace that describes that index
              const imagesField = `${f.field}.${associatedWorkspace.config.dataMap?.images ?? `images`}`,
                label = this.catalog.labelForField(f),
                id = associatedWorkspace?._id ?? f.field

              if (!config.catalog.imageSources.some(is => is.label === label || is.id == id)) { //)is.field === imagesField || _.includes(is.fields, imagesField))) {
                const imageSource = {id, field: imagesField, label, index: f.esIndex, workspace: associatedWorkspace.config}

                config.catalog.imageSources.push(imageSource)
                DEBUG_WORKSPACES && console.log(`Added image source '${imageSource.label}' for ${imageSource.id}`, imageSource)
              }

              // Add libraries owned by the remote workspace
              if (!associatedWorkspace.catalog.features.noSelfLibrary && f.library?.hide !== true) {
                const siteLibraryLabel = `${associatedWorkspace.config.title}`
                const indexLibraries: Array<LibraryItemConfig> = [
                  !(associatedWorkspace.config.libraries ?? []).some(l => l.label == siteLibraryLabel) ? {
                    addedBySanitizer: true,
                    index: associatedWorkspace.config.catalog.index,
                    label: siteLibraryLabel,
                    name: associatedWorkspace._id,
                    workspaceId: associatedWorkspace._id
                  } : null,
                  // Add the connected workspace libraries to our own
                  ...(associatedWorkspace.config.libraries ?? [])
                    .filter(l => l.index === f.esIndex)       // Limit to libraries directly owned by that workspace only
                    .map(l => {
                      return ({...l, addedBySanitizer: true, workspaceId: l.workspaceId ?? associatedWorkspace._id, assignTo: null})
                    })
                ].filter(_.identity)

                indexLibraries.forEach((il) => {
                  if (!config.libraries.find(l => (l.name === il.name) && l.workspaceId === il.workspaceId)) {
                    // console.debug(`Adding library ${f.label} to workspace ${config.title}`)
                    config.libraries.push({addedBySanitizer: true, ...il, index: il.index ?? associatedWorkspace.catalog.index, assignTo: il.assignTo ? f.field : undefined})
                  }
                })
              }

              (config.libraries ?? []).forEach(l => {
                if (l.index && !l.assignTo && !l.assignTo2 && l.index === f.esIndex) {
                  const assignToField = this.catalog.assignToForIndex(l.index)?.field

                  l.assignTo = assignToField ?? f.field
                }
              })

              config.libraries = _.uniqBy(config.libraries, l => LibraryItemConfigWrapperStore.idFor(catalog, l))
            })
        })

      if (!config.sections) {
        config.sections = []
      }

      if (!config.sections.some(s => s.id === config._id)) {
        const section = {id: config._id, label: config.itemName ?? config.title}
        config.sections.push(section)
        DEBUG_WORKSPACES && console.log(`Created section ${section.label}`)
      }

      // Import fields from other workspaces
      if (config.catalog?.index) {
        const alreadyImportedIndexes = new Set<string>([config.catalog.index])
        // _.orderBy(
        //   config.catalog.fields.filter(f => f.esIndex && !f.hidden),
        //   f => this.catalog.labelForField(f)
        // )
        config.catalog.fields.filter(f => f.esIndex && !f?.__sanitizer?.imported).forEach((esLocalField: ICatalogFieldConfig) => {
          const fieldAndIndex = `${esLocalField.field}.${esLocalField.esIndex}`
          if (!alreadyImportedIndexes.has(fieldAndIndex) || (esLocalField.__sanitizer?.includeDuplicateIndexes)) {
            const fkWorkspace = this.catalog.workspaceByIndex[esLocalField.esIndex]
            alreadyImportedIndexes.add(fieldAndIndex)
            if (fkWorkspace && !fkWorkspace.noImportExternalAttributes) {
              const fkWorkspaceStore = workspaces.wrapperById[fkWorkspace._id]
              if (!fkWorkspaceStore.sanitized) {
                fkWorkspaceStore._sanitizeConfig()
              }

              const remoteImageField = fkWorkspaceStore.dataMap?.images ?? 'images'

              let section = esLocalField.section

              // Add sections to fields with a foreign key (esIndex + workspace)
              if (!esLocalField.section && esLocalField.section !== null) {
                const sectionId = this.catalog.sectionIdForField(esLocalField, fkWorkspace),
                  sectionConfig = this.catalog.sectionConfigFor(sectionId)

                if (sectionConfig) {
                  section = sectionId
                  // f.section = section.id
                } else {
                  const newSection = this.catalog.newSectionFor(esLocalField, {depth: 0})
                  section = sectionId

                  DEBUG_WORKSPACES && console.log(`Create section ${config.title} -> ${newSection.label} | due to ${fkWorkspace._id}.${esLocalField.field} (${fkWorkspace.catalog?.index})`, toJS({newSection, f: esLocalField}, {recurseEverything: true}))
                  config.sections = [...(config.sections ?? []), newSection]
                }

                esLocalField.section = sectionId
              }

              // Add any associated fields from the other workspace
              if (esLocalField.section != null && esLocalField.__sanitizer?.importChildren !== false) {
                const path = esLocalField.esField ?? esLocalField.field, {fieldsByName} = this,
                  parentSection = config.sections.find(s => s.id === esLocalField.section),
                  childFieldsToImport = new Set<string>((esLocalField.include ?? []).map(v => `${esLocalField.field}.${v}`)),
                  exclude = new Set<string>(esLocalField.exclude ?? [])

                // logJS({ws: this._id, path, parentSection, f})

                type ForeignKeyDescriptor = { section?: string }
                let nestedForeignKeys = new Map<string, ForeignKeyDescriptor>()

                let ignorePrefixes = new Set<string>()
                exclude.size > 0 && DEBUG_WORKSPACES && console.groupCollapsed(`Excluding fields from '${esLocalField.field}' [${Array.from(exclude.keys()).join(', ')}]`)
                const linkedFields = (fkWorkspace.catalog.fields ?? [])
                  .filter(({field}) => {
                    const r = !(exclude.has(field) || (exclude.size > 0 && esLocalField.exclude.some(v => field.startsWith(`${v}.`))))
                    DEBUG_WORKSPACES && !r && console.log(`- ${field}`)
                    return r
                  })
                  .filter(f1 => {
                    const subFieldAndIndex = `${fieldAndIndex}.${f1.field}.${f1.esIndex}`
                  if (shouldIgnoreCatalogField(fkWorkspace, f1.field) || f1.esIndex === config.catalog.index) {
                    ignorePrefixes.add(f1.field)
                    // In dev we import style (but exclude .colorways) and import colorway
                    //f1.esIndex != null && alreadyImportedIndexes.add(f1.esIndex)
                    return false
                  }

                  if (Array.from(ignorePrefixes.keys()).some(k => f1.field.startsWith(`${k}.`))) {
                    // console.log(`ignoring field ${f1.field}`, {ignorePrefixes, childFieldsToImport})
                    f1.esIndex != null && alreadyImportedIndexes.add(subFieldAndIndex)
                    return false
                  }

                  const fullName = `${path}.${f1.field}`,
                    depth = (parentSection ? parentSection.depth : 0) + 1

                  // console.log({parentSection, depth, fullName})
                  if (_.isNumber(config.catalog?.features?.maxDepthToAutoTraceFields) && config.catalog?.features?.maxDepthToAutoTraceFields < depth) return false

                  if ((childFieldsToImport.size > 0 && !childFieldsToImport.has(fullName))) {
                    f1.esIndex != null && alreadyImportedIndexes.add(subFieldAndIndex)
                    return false
                  }

                  // Already imported?
                  if (fieldsByName[fullName] || (f1.esIndex && alreadyImportedIndexes.has(subFieldAndIndex))) {
                    f1.esIndex != null && alreadyImportedIndexes.add(subFieldAndIndex)
                    return false
                  }

                  const wsWrapper = workspaces.wrapperById[fkWorkspace._id]
                  // if (!wsWrapper.sanitized) return false

                  f1.esIndex != null && alreadyImportedIndexes.add(f1.esIndex)

                  const remotePath = wsWrapper.catalog.pathToFieldName(f1.field),
                    localPath = catalog.pathToFieldName(fullName)

                  // console.log(toJS({remotePath, localPath, fullName, f1}, {recurseEverything: true}))
                  return !remotePath.some(f => localPath.some(local => local.esIndex == f.esIndex))

                  /*  && (!f1.section || f1.section === ws.catalog.index || f1.esIndex) */
                })

                exclude.size > 0 && DEBUG_WORKSPACES && console.groupEnd()

                if (!_.isEmpty(linkedFields)) {
                  DEBUG_WORKSPACES && console.groupCollapsed(`Adding ${linkedFields.length} linked fields from workspace '${fkWorkspace.title}' via field '${path}'`, toJS({linkedFields, parentSection, section, ws: fkWorkspace, f: esLocalField, self: this}))

                  linkedFields.forEach(
                    associatedField => {
                      const fullName = `${path}.${associatedField.field}`

                      let importedField: ICatalogFieldConfig = {
                        ..._.cloneDeep(associatedField),
                        field: fullName,
                        section: associatedField.esIndex ? parentSection?.id : associatedField.section ?? section, // parentSection ? parentSection.field != fullName ? parentSection.id : section : section,
                        __sanitizer: {imported: true, importChildren: childFieldsToImport.size === 0, includeDuplicateIndexes: associatedField.includeDuplicateIndexes}
                      }

                      // YFTS-9748 - Defaults from other catalogs shouldn't be directly shown on our board if we aren't showing the nested card (otherwise children fields will pile up on our card)
                      if (importedField?.card?.default) {
                        if (esLocalField.card?.defaultChildren && !esLocalField.card.defaultChildren.includes(associatedField.field)) {
                          // console.log(`Unset default card field - ${importedField.field}`, {esLocalField})
                          importedField.card.default = false
                        }
                      }

                      // if (fullName === 'styleCandidates.colorways') debugger
                      // Is this field a foreign key to another workspace?
                      // Something like we're processing concepts.shapes on the concepts board and importing fields from the Shapes workspace and there's shapes.coloredMaterials, what 'section' should this be?
                      if (importedField.esIndex && (childFieldsToImport.size === 0 || Array.from(childFieldsToImport.keys()).some(childrenToImport => childrenToImport.startsWith(`${importedField.field}.`)))) {
                        const newSection = this.catalog.newSectionFor(importedField, {depth: parentSection?.depth + 1})
                        if (!newSection) {
                          DEBUG_WORKSPACES && console.debug(`No section created for remote index ${fullName} no workspace exists that shows these items`)
                        } else {
                          if (newSection && parentSection) {
                            newSection.id = `${parentSection.id}.${associatedField.field}`
                            newSection.label = parentSection ? `${pluralize(newSection.label, 1)} (${pluralize(parentSection.label, 1)})` : pluralize(newSection.label, 1)
                            _.merge(newSection, this.catalog.features.sectionOverrides[newSection.id])
                          }

                          DEBUG_WORKSPACES && console.debug(`Create section ${config.title} -> ${newSection.label} | due to ${fkWorkspace._id}.${esLocalField.field} (${fkWorkspace.catalog?.index})`, toJS({parentSection, newSection, f: esLocalField}, {recurseEverything: true}))
                          newSection && (config.sections = [...(config.sections ?? []), newSection])

                          if (newSection) {
                            //importedField.section = newSection.id
                            nestedForeignKeys.set(importedField.field, {section: newSection.id})
                          }
                        }

                        if (importedField.renderer != 'text' && !config.catalog.imageSources.some(is => is.id === fullName) && importedField?.def) {
                          const imageSource: CatalogImageSourceConfig = {id: fullName, field: `${fullName}.${remoteImageField}`, label: associatedField.label, index: associatedField.esIndex}
                          config.catalog.imageSources.push(imageSource)
                          DEBUG && DEBUG_WORKSPACES && console.log(`Added image source '${imageSource.label}' - ${imageSource.id}`, imageSource)
                        }
                      } else { // Find our nearest ancestor field that has a remote index and use their section
                        const match = _.maxBy(Array.from(nestedForeignKeys.keys()).filter(k => importedField.field.startsWith(k)), k => k.length)

                        if (match) {
                          importedField.section = nestedForeignKeys.get(match).section
                          // Why?  - removes deep fields from view options - gant complained
                          //importedField.card = {...(importedField.card ?? {})} //, hide: true}
                        }
                      }

                      esLocalField.mutateImportedField && esLocalField.mutateImportedField(importedField, {field: esLocalField, workspace: this}, globalStores)

                      config.catalog.fields.push(importedField)
                      DEBUG_WORKSPACES && console.log(`Workspace: ${this.title} | Field: ${fullName} |  Section: ${importedField.section} - Our Section: ${parentSection?.id}`, toJS({importedField, associatedField, parentSection}, {recurseEverything: true}))
                    })

                  DEBUG_WORKSPACES && console.groupEnd()
                }
              }
            }
          }
        })

        config.sections = _.uniqBy(config.sections, s => s.id)

        const {catalog: {index}} = config
        const {deploymentConfig: {detailViews}} = this
        if (!detailViews[index]) {
          detailViews[index] = this.defaultDetailView
        }

        const detailViewForIndex = detailViews[index]

        detailViewForIndex.modal = detailViewForIndex.modal || this.defaultModalDetailView
        detailViewForIndex.sidebar = detailViewForIndex.sidebar || this.defaultSidebarDetailView
      }

      config.sections = config.sections.map(_.identity)

      config.catalog.fields.forEach(f => {
        if (f.sort && f?.def?.enumValues && !f.sort.custom) {
          _.merge(f.sort, {custom: {_key: {order: f.def.enumValues}}})
        }
      })

      //YFTS-10185 - Duplicate Attribute clean up for Group By, Filter, Attribute Tags - each section should have unique field by name for now filtered by unique section + label
      // Commented out by NVS - this breaks dev, RH, etc.  You cannot just restrict on label + section and you have to guard against the sanitizer just adding the fields you deleted right back in - I'm seeing infinite loops breaking _everything_ on RH and Dev
      // const _fields = config.catalog.fields,
      //   groupedBySection = _.groupBy(config.catalog.fields, f => stringify(_.pick(f, ['section', 'label']))),
      //   filteredFields = _fields.filter(field => !field?.section || groupedBySection[stringify(_.pick(field, ['section', 'label']))][0] === field)
      //
      // if (config.catalog.fields.length != filteredFields.length) {
      //   console.log({lookups: groupedBySection, before: toJS(config.catalog.fields), after: toJS(filteredFields, {recurseEverything: true})})//, diff: diff(toJS(config.catalog.fields), toJS(filteredFields))})
      //
      //   DEBUG && console.warn(`Duplicate fields found in workspace - only the first will be used! FIX YOUR CONFIG! - ${workspaceConfig.title}`, {duplicates: toJS(_.values(groupedBySection).filter(v => v.length > 1), {recurseEverything: true}))
      //   config.catalog.fields = filteredFields
      // }

      // YFTS-8790 - multi-select instead
      // if (config.catalog.imageSources.length > 1 && !config.catalog.imageSources.some(is => is.label === 'All')) {
      //   const imageSource = {id: 'all', fields: config.catalog.imageSources.map(is => is.field).filter(_.identity), label: 'All'}
      //   if (imageSource.fields.length > 1) {
      //     config.catalog.imageSources = [imageSource, ...(config.catalog.imageSources ?? [])]
      //     DEBUG && LOG_SANITIZE && console.log(`Added image source '${imageSource.label}' - ${imageSource.id}`, imageSource)
      //   }
      // }

      DEBUG && DEBUG_WORKSPACES && console.groupEnd()
    }
  }

  @computed get id() { return this.config?._id}

  @computed get isCurrentWorkspace() {
    return this.id === this.workspaces.workspace?.id
  }

  @computed get presets(): Array<ICatalogPresetES> {
    return this.catalog.presets.presetsByWorkspace[this.id] ?? []
  }

  @computed get defaultDetailView(): DetailsViewConfig {
    const {defaultModalDetailView: modal, defaultSidebarDetailView: sidebar, config: {title, catalog: {imageSources, index}}} = this
    return {
      modal, sidebar,
      allow: {uploadImages: true},
      syncId: index,
      imageSources
    }
  }

  @computed get defaultModalDetailView(): ModalConfig {
    const {
      catalog, workspaces,
      catalog: {sectionById, librariesWithWorkspaces, _fieldByName}, config: {itemName, title}, defaultDetailFieldBySection, defaultDetailFieldsForSection,
      dataMap
    } = this

    return removeUndefinedsNested({
      h1: {
        field: dataMap?.title
      },
      lastSync: {label: 'Synced:', field: 'exLastSync', type: 'sync'},
      notes: {label: 'Notes', field: 'attributes.VIP_Notes'},
      status: _fieldByName['attributes.VIP_Status'] ? {
        field: 'attributes.VIP_Status'
      } : undefined,
      tabs: [
        {
          id: 'default',
          label: pluralize(title ?? itemName, 1),
          preload: true,
          columns: 3,
          sections: _.entries(defaultDetailFieldBySection).map(([k, v]) => {
            const fields = defaultDetailFieldsForSection(k)

            return ({
              id: k,
              defaultCollapsed: _.keys(defaultDetailFieldBySection).length > 1,
              label: !k ? title : (sectionById[k]?.label ?? toHeaderCase(k)),
              columns: _.first(fields)?.fields ? 1 : 3,
              fields
            })
          })
        },
        !_.isEmpty(librariesWithWorkspaces) && {
          id: 'specs',
          label: 'Specifications',
          columns: 2,
          fields: librariesWithWorkspaces.map(l => ({field: l.assignTo}))
        },
        ...(true ? [] : _.flatMap((librariesWithWorkspaces ?? []).map(l => {
          const libraryWorkspace = workspaces.workspaceForLibrary(l),
            assignToFields = catalog.fieldByIndex[l.index]

          return (assignToFields ?? []).map((f) => ({
            id: `${l.name}-details`,
            label: catalog.labelForField(f),
            columns: 3,
            countField: f.field,
            defaultCollapsed: true,
            fields: [
              {
                field: f.field,
                type: 'cards',
                titleField: 'name',
                fields: (defaultDetailFieldBySection[l.index] ?? []).map(f => _.pick(f, 'field'))
              }
            ]
          } as ModalTabConfig))
        }))),
        /*
         // {
        //   id: 'colorways',
        //   label: 'Samples',
        //   create: false,
        //   countField: 'style.colorways',
        //   fields: [
        //     {
        //       field: 'style.colorways',
        //       type: 'cards',
        //       titleField: 'name',
        //       has3d: true,
        //       field3d: 'attributes.GNT_Colorway_DesignChangeComments_String',
        //       fields: [
        //         {label: 'FOB Cost', field: 'attributes.GNT_Colorway_TargetFOBPrice_Double', mutateC8: true, def: {type: 'Float'}},
        //         {label: 'Active', field: 'attributes.Active', mutateC8: true, def: {type: 'Boolean'}},
        //         {label: 'Key Color', field: 'attributes.GNT_ColorwayKeyLook_Boolean', mutateC8: true, def: {type: 'Boolean'}},
        //       ]
        //     }
        //   ]
        },
         */
        // ...(librariesWithWorkspaces ?? []).map(l => {
        //   return ({
        //     id: l.name,
        //     label: catalog.labelForLibrary(l.name),
        //     columns: 3,
        //     sections: [
        //       {
        //         label: '',
        //         columns: 3,
        //         fields: (defaultDetailFieldBySection[l.index] ?? []).map(f => _.pick(f, 'field'))
        //       }
        //     ]
        //   })
        // }).filter(_.identity),

        // {
        //   id: 'annotations', label: 'Annotations', columns: 1,
        //   sections: [
        //     {
        //       label: 'Image Annotation',
        //       fields: [{type: 'annotate-images', }]
        //     }
        //     ]
        // },

        ui.settings.defaults.catalog.comments === false ? null : {
          id: 'comments',
          label: 'Comments',
          fields: [{label: null, type: 'comments', field: 'comments'}]
        },
        {
          id: 'history', type: 'changelog', label: 'Change History', columns: 1
        }
        // {
        //   id: '3d', label: '3D Model', columns: 1,
        //   sections: [
        //     {
        //       label: '3d Model',
        //       // defaultCollapsed: true,
        //       fields: [{type: '3d-emersya', field: 'colorway.attributes.VIP_3DLink', screenshotsField: 'colorway.attributes.VIP_3Dscreenshots'}]
        //     },
        //     {
        //       label: '3d Screenshots',
        //       // defaultCollapsed: true,
        //       fields: [{field: 'colorway.attributes.VIP_3Dscreenshots', label: false, type: 'inline-images'}]
        //     }
        //   ]
        // }

      ].filter(_.identity)
    })
  }

  @computed get defaultDetailFields(): ICatalogFieldConfig[] {
    const {config: {catalog: {fields}, seasonSelectors}} = this

    return fields.filter(f => f.field && !f.agg && !f.card?.hide && !shouldIgnoreCatalogField(this.config, f.field))
  }

  @computed get defaultDetailFieldBySection(): { [sectionId: string]: Array<ICatalogFieldConfig> } {
    const {config: {}} = this

    return _.groupBy(this.defaultDetailFields, f => f.section ?? (f.esIndex ? this.catalog.sectionIdForField(f, this.config) : ''))
  }

  @computed get defaultSidebarDetailView(): SidebarConfig {
    const {catalog: {sectionById}, defaultDetailFieldsForSection, defaultDetailFieldBySection, config: {title}} = this
    return {
      sections: _.entries(defaultDetailFieldBySection).map(([k, v]) => ({
        label: (k ? sectionById[k]?.label : title) ?? title,
        fields: defaultDetailFieldsForSection(k)
      }))
    }
  }

  defaultDetailFieldsForSection = (section: string): Array<DetailsViewFieldConfig> => {
    const v = this.defaultDetailFieldBySection[section]

    // For ref list fields we want to create a single detail view field with all the other displayble fields as a child property so that field renderer can handle each set of fields itself (especially for 1-many spec relationships)

    const refListField = v.find(f => f.def?.type === 'RefList')

    if (!refListField) {
      return v.map(f => _.pick(f, 'field'))
    } else {
      return [
        {
          field: refListField.field,
          label: section,
          type: 'cards',
          titleField: 'name',
          fields: v.filter(f1 => f1.field !== refListField.field).map(f => ({field: f.field.substring(refListField.field.length + 1)}))
        }
      ]
    }
  }

  urlForField = (f: ICatalogFieldConfig) => {
    return this.url
  }
}

/*
const imageSourceFieldNested = (is: CatalogImageSourceConfig, parent?: CatalogImageSourceConfig, path?: string) => {
  const prefix = `${path ? `${path}` : ''}${parent ? `${parent.field.replaceAll('.images', '')}.` : ''}`
  if (is?.workspace) {
    if (prefix.indexOf(`${is.field}.`) != -1) {
      const remoteWorkspace = workspaces.wrapperById[is.workspace._id]
      if (remoteWorkspace) {
        //console.log(toJS({path, prefix, is, parent, remoteWorkspace: remoteWorkspace._id}, {recurseEverything: true}))
        return [is.field ? `${prefix}${is.field}` : null, ..._.flatMap((remoteWorkspace.config.catalog.imageSources ?? []).map(ris => imageSourceFieldNested(ris, is, prefix)))].filter(_.identity)
      }
    }
  }

  return is.field ? `${prefix}${is.field}` : null
}*/



// WEBPACK FOOTER //
// ./src/stores/workspace/Workspace.ts